import React, { useState } from 'react';
import { Gavel, Plus, Vote, MessageSquare, Trophy, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useDebates } from '@/hooks/social/useDebates';
import { Debate, DebateArgument } from '@/services/social/debatesService';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';

interface DebateSectionProps {
  interviewId: string;
}

interface DebateCardProps {
  debate: Debate;
  onAddArgument: (debateId: string, argumentText: string, anonymousName?: string) => Promise<unknown>;
  onVote: (debateId: string, votedForUserId: string) => void;
  currentUserId?: string;
}

const DebateCard: React.FC<DebateCardProps> = ({
  debate,
  onAddArgument,
  onVote,
  currentUserId,
}) => {
  const [argumentText, setArgumentText] = useState('');
  const [anonymousName, setAnonymousName] = useState('');
  const [showArgumentForm, setShowArgumentForm] = useState(false);
  const { t } = useTranslation();

  const isOpenDebate = !debate.invited_user_id;
  const isParticipant = currentUserId === debate.creator_user_id || currentUserId === debate.invited_user_id;
  const canAddArgument = debate.status === 'active' && (isOpenDebate || isParticipant);

  const handleAddArgument = async () => {
    if (!argumentText.trim()) return;
    try {
      await onAddArgument(
        debate.id,
        argumentText.trim(),
        currentUserId ? undefined : anonymousName.trim() || undefined
      );
      // Only reset the UI after we know the insert succeeded
      setArgumentText('');
      setAnonymousName('');
      setShowArgumentForm(false);
    } catch {
      // Keep the text so the user doesn't lose what they wrote
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return t('debates.status.pending');
      case 'active': return t('debates.status.active');
      case 'completed': return t('debates.status.completed');
      case 'cancelled': return t('debates.status.cancelled');
      default: return status;
    }
  };

  // Calculate votes
  const creatorVotes = debate.votes?.filter(v => v.voted_for_user_id === debate.creator_user_id).length || 0;
  const invitedVotes = debate.votes?.filter(v => v.voted_for_user_id === debate.invited_user_id).length || 0;
  const userHasVoted = debate.votes?.some(v => v.voter_user_id === currentUserId);

  const getDisplayName = (argument: DebateArgument) => {
    if (argument.profiles?.display_name && argument.profiles.display_name !== 'Unknown') {
      return argument.profiles.display_name;
    }
    if (argument.anonymous_name) {
      return argument.anonymous_name;
    }
    return t('debates.roles.anonymous');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Gavel className="h-5 w-5" />
              <span className="font-semibold">{t('debates.title')}</span>
              <Badge className={getStatusColor(debate.status)}>
                {getStatusText(debate.status)}
              </Badge>
            </div>
            <p className="text-sm font-medium">{debate.assertion}</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Participants */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={debate.creator_profile?.avatar_url} />
                <AvatarFallback>C</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">
                  {debate.creator_profile?.display_name || t('debates.roles.unknown')}
                </p>
                <p className="text-xs text-muted-foreground">{t('debates.roles.creator')}</p>
              </div>
            </div>
            {!userHasVoted && currentUserId && currentUserId !== debate.creator_user_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onVote(debate.id, debate.creator_user_id)}
                className="w-full"
              >
                <Vote className="h-3 w-3 mr-1" />
                {t('debates.actions.vote')} ({creatorVotes})
              </Button>
            )}
            {userHasVoted && (
              <div className="text-xs text-center text-muted-foreground">
                {creatorVotes} {t('debates.votes')}
              </div>
            )}
          </div>

          <div className="space-y-2">
            {debate.invited_user_id ? (
              <div className="flex items-center space-x-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={debate.invited_profile?.avatar_url} />
                  <AvatarFallback>I</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">
                    {debate.invited_profile?.display_name || debate.invited_username}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('debates.roles.invited')}</p>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground text-sm">
                {t('debates.actions.waitingForOpponent')}
              </div>
            )}
            
            {debate.invited_user_id && !userHasVoted && currentUserId && currentUserId !== debate.invited_user_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onVote(debate.id, debate.invited_user_id)}
                className="w-full"
              >
                <Vote className="h-3 w-3 mr-1" />
                {t('debates.actions.vote')} ({invitedVotes})
              </Button>
            )}
            {debate.invited_user_id && userHasVoted && (
              <div className="text-xs text-center text-muted-foreground">
                {invitedVotes} {t('debates.votes')}
              </div>
            )}
          </div>
        </div>

        {/* Arguments */}
        {debate.arguments && debate.arguments.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center">
                <MessageSquare className="h-4 w-4 mr-1" />
                {t('debates.arguments')} ({debate.arguments.length})
              </h4>
              
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {debate.arguments
                  .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                  .map((argument) => (
                    <div key={argument.id} className="text-sm space-y-1">
                      <div className="flex items-center space-x-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={argument.profiles?.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {getDisplayName(argument)?.[0] || 'A'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-xs">
                          {getDisplayName(argument)}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          #{argument.argument_number}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(argument.created_at), 'HH:mm', { locale: enUS })}
                        </span>
                      </div>
                      <p className="text-sm pl-8">{argument.argument_text}</p>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}

        {/* Add argument form - show for open debates or participants */}
        {canAddArgument && (
          <>
            <Separator />
            {showArgumentForm ? (
              <div className="space-y-3">
                {!currentUserId && (
                  <Input
                    value={anonymousName}
                    onChange={(e) => setAnonymousName(e.target.value)}
                    placeholder={t('debates.actions.yourName')}
                    className="mb-2"
                  />
                )}
                <Textarea
                  value={argumentText}
                  onChange={(e) => setArgumentText(e.target.value)}
                  placeholder={t('debates.actions.writeArgument')}
                  className="min-h-[80px]"
                />
                <div className="flex space-x-2">
                  <Button onClick={handleAddArgument} disabled={!argumentText.trim()}>
                    {t('debates.actions.addArgument')}
                  </Button>
                  <Button variant="outline" onClick={() => setShowArgumentForm(false)}>
                    {t('debates.actions.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowArgumentForm(true)}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('debates.actions.respondToDebate')}
              </Button>
            )}
          </>
        )}

        <div className="text-xs text-muted-foreground">
          {t('debates.createdAt')} {format(new Date(debate.created_at), 'MM/dd/yyyy HH:mm', { locale: enUS })}
        </div>
      </CardContent>
    </Card>
  );
};

const CreateDebateDialog: React.FC<{
  interviewId: string;
  onCreateDebate: (assertion: string, invitedUsername?: string) => void;
  isCreating: boolean;
}> = ({ interviewId, onCreateDebate, isCreating }) => {
  const [assertion, setAssertion] = useState('');
  const [invitedUsername, setInvitedUsername] = useState('');
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = () => {
    if (!assertion.trim()) return;
    onCreateDebate(assertion.trim(), invitedUsername.trim() || undefined);
    setAssertion('');
    setInvitedUsername('');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {t('debates.startNew')}
        </Button>
      </DialogTrigger>
      
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('debates.createNew')}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">{t('debates.assertion')}</label>
            <Textarea
              value={assertion}
              onChange={(e) => setAssertion(e.target.value)}
              placeholder={t('debates.assertionPlaceholder')}
              className="mt-1"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium">{t('debates.inviteUser')}</label>
            <Input
              value={invitedUsername}
              onChange={(e) => setInvitedUsername(e.target.value)}
              placeholder={t('debates.usernamePlaceholder')}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('debates.openDebateNote')}
            </p>
          </div>
          
          <div className="flex space-x-2">
            <Button onClick={handleSubmit} disabled={!assertion.trim() || isCreating}>
              {isCreating ? t('debates.creating') : t('debates.create')}
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('debates.cancel')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DebateSection: React.FC<DebateSectionProps> = ({ interviewId }) => {
  const { 
    debates, 
    isLoading, 
    createDebate, 
    addArgumentAsync,
    vote,
    isCreatingDebate,
  } = useDebates(interviewId);

  const [currentUserId, setCurrentUserId] = useState<string>();
  const { t } = useTranslation();

  React.useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id);
    };
    getCurrentUser();
  }, []);

  const handleCreateDebate = (assertion: string, invitedUsername?: string) => {
    createDebate({
      interview_id: interviewId,
      assertion,
      invited_username: invitedUsername,
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="space-y-3">
              <div className="h-32 bg-muted rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Gavel className="h-5 w-5" />
            <span>{t('debates.title')}</span>
            {debates.length > 0 && (
              <Badge variant="secondary">
                {debates.length}
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <CreateDebateDialog
          interviewId={interviewId}
          onCreateDebate={handleCreateDebate}
          isCreating={isCreatingDebate}
        />
        
        {debates.length > 0 ? (
          <div className="space-y-4">
            {debates.map((debate) => (
              <DebateCard
                key={debate.id}
                debate={debate}
                  onAddArgument={addArgumentAsync}
                onVote={vote}
                currentUserId={currentUserId}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Gavel className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('debates.noDebates')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};